import { Link } from "react-router-dom";
import { ExternalLink, TriangleAlert } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import {
  nextgenAdminConversationPath,
  nextgenAdminEvalRunPath,
} from "~/pages/manifold-nextgen/paths";
import { EVAL_SCORE_MAX, type EvaluatorScore } from "~/services/evals";
import { EVALUATORS } from "./constants/evaluators";
import { CaseOutcome } from "./constants/scoring";
import { OutcomeBadge } from "./primitives/badges";
import { TagChips } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration } from "./primitives/format";
import { CaseQuery, Section, Spec, SpecRow } from "./primitives/layout";
import { outcomeForResults, splitCaseInputs } from "./selectors/cases";
import { resultDurationSeconds } from "./selectors/core";
import { useEvalCase, useEvalCaseResult } from "./useEvalApi";

interface CaseResultDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string | null;
  /** The iteration to open in full. Sibling iterations are listed alongside. */
  resultId: string | null;
  caseId: string | null;
  /** Every iteration of this case in this run, for the iteration switcher. */
  siblingResultIds: string[];
  onSelectResult: (resultId: string) => void;
}

/**
 * One case's outcome in one run, in full: the question and expectation above each
 * judge's score *and its stated reason*, with the real conversation one click
 * away. A score with no reason and no transcript is not reviewable.
 */
export const CaseResultDrawer = ({
  open,
  onOpenChange,
  runId,
  resultId,
  caseId,
  siblingResultIds,
  onSelectResult,
}: CaseResultDrawerProps) => {
  const { data: evalCase } = useEvalCase(caseId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="border-b border-border pr-12">
          <SheetTitle className="font-mono text-sm break-all">
            {evalCase?.name ?? "Case result"}
          </SheetTitle>
          <SheetDescription>
            {evalCase?.description ?? "One case's outcome in this run."}
          </SheetDescription>
          {evalCase && evalCase.tags.length > 0 && (
            <TagChips tags={evalCase.tags} className="mt-1" />
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          <CaseResultView
            runId={runId}
            resultId={resultId}
            caseId={caseId}
            siblingResultIds={siblingResultIds}
            onSelectResult={onSelectResult}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface CaseResultViewProps {
  runId: string | null;
  resultId: string | null;
  caseId: string | null;
  /** Every iteration of this case in this run, for the iteration switcher. */
  siblingResultIds: string[];
  onSelectResult: (resultId: string) => void;
  /** The run's start time, which names the run where the caller has no title for it. */
  startedAt?: string;
}

/**
 * The body of one case's outcome in one run, without a frame of its own, so the
 * result drawer and the case drawer's Runs tab show the same thing.
 */
export const CaseResultView = ({
  runId,
  resultId,
  caseId,
  siblingResultIds,
  onSelectResult,
  startedAt,
}: CaseResultViewProps) => {
  const { data: result, error, isLoading } = useEvalCaseResult(runId, resultId);
  const { data: evalCase } = useEvalCase(caseId);

  const { query } = splitCaseInputs(evalCase?.inputs);
  const outcome = result ? outcomeForResults([result]).outcome : CaseOutcome.NOT_RUN;

  return (
    <>
      {isLoading && !result ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Loading case result">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      ) : error || !result ? (
        <ResourceLoadError error={error} resourceLabel="case result" className="py-16" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {startedAt && (
              <h3 className="text-sm font-medium whitespace-nowrap text-foreground">{startedAt}</h3>
            )}
            <OutcomeBadge outcome={outcome} />
            <ScoreValue score={result.score} className="text-sm font-medium" />
            <span className="text-xs text-muted-foreground">
              {formatDuration(resultDurationSeconds(result))}
            </span>
            <div className="ml-auto flex items-center gap-4">
              {/* The way from a grid cell to the run that produced it,
                      named rather than left to a mark too small to read. */}
              {runId && (
                <Link
                  to={nextgenAdminEvalRunPath(runId)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                >
                  Open run
                  <ExternalLink className="size-3.5" aria-hidden />
                </Link>
              )}
              {result.conversation_id ? (
                <Link
                  to={nextgenAdminConversationPath(result.conversation_id)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                >
                  Open transcript
                  <ExternalLink className="size-3.5" aria-hidden />
                </Link>
              ) : (
                <span className="text-xs text-muted-foreground">No transcript kept</span>
              )}
            </div>
          </div>

          {siblingResultIds.length > 1 && (
            <IterationSwitcher
              resultIds={siblingResultIds}
              activeId={result.id}
              onSelect={onSelectResult}
            />
          )}

          {result.error && (
            <section className="rounded-md border border-red-300 bg-red-50 p-3">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
                <TriangleAlert className="size-3.5" aria-hidden />
                The case errored
              </h3>
              <p className="mt-1.5 font-mono text-[11px] break-words text-red-700">
                {result.error}
              </p>
            </section>
          )}

          {query && (
            <Section title="Question">
              <CaseQuery query={query} />
            </Section>
          )}

          {evalCase?.expectations && Object.keys(evalCase.expectations).length > 0 && (
            <Section title="What it expected">
              <KeyValueList record={evalCase.expectations} />
            </Section>
          )}

          <Section
            title="How each evaluator judged it"
            subtitle={`Judge scores run 1-${EVAL_SCORE_MAX}; assertions are pass or fail.`}
          >
            {result.evaluator_scores && Object.keys(result.evaluator_scores).length > 0 ? (
              <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                {Object.entries(result.evaluator_scores).map(([name, score]) => (
                  <EvaluatorRow key={name} name={name} score={score} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                This result recorded no evaluator detail.
              </p>
            )}
          </Section>

          <Section title="Provenance">
            <Spec>
              <SpecRow label="Case version">
                <span className="tabular-nums">v{result.case_version}</span>
                {evalCase && evalCase.version !== result.case_version && (
                  <span className="ml-1.5 text-xs text-orange-700">
                    the case has since been edited to v{evalCase.version}
                  </span>
                )}
              </SpecRow>
              <SpecRow label="Iteration">
                <span className="tabular-nums">{result.iteration + 1}</span>
              </SpecRow>
              {result.metrics &&
                Object.entries(result.metrics).map(([metric, value]) => (
                  <SpecRow key={metric} label={humanizeKey(metric)}>
                    <span className="tabular-nums">
                      {typeof value === "number" ? value.toFixed(2) : String(value)}
                    </span>
                  </SpecRow>
                ))}
            </Spec>
          </Section>
        </div>
      )}
    </>
  );
};

/**
 * One evaluator's judgement, score and stated reason together. The reason is not
 * truncated behind a toggle — it is the only thing that explains a score. Rows
 * wear body weight, so five evaluators do not read as five more headings.
 */
const EvaluatorRow = ({ name, score }: { name: string; score: EvaluatorScore }) => (
  <li className="px-3 py-2">
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-sm text-foreground" title={name}>
        {evaluatorLabel(name)}
      </span>
      <EvaluatorValue score={score} />
    </div>
    {score.reason && (
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{score.reason}</p>
    )}
  </li>
);

const EvaluatorValue = ({ score }: { score: EvaluatorScore }) => {
  if (score.error) {
    return <span className="shrink-0 text-xs font-medium text-red-700">Evaluator errored</span>;
  }
  if (typeof score.value === "boolean") {
    return (
      <span
        className={cn(
          "shrink-0 text-xs font-medium",
          score.value ? "text-green-700" : "text-red-700"
        )}
      >
        {score.value ? "Pass" : "Fail"}
      </span>
    );
  }
  if (typeof score.value === "number") {
    return <ScoreValue score={score.value} className="shrink-0 text-xs" />;
  }
  return <span className="shrink-0 text-xs text-muted-foreground">—</span>;
};

const IterationSwitcher = ({
  resultIds,
  activeId,
  onSelect,
}: {
  resultIds: string[];
  activeId: string;
  onSelect: (resultId: string) => void;
}) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-muted-foreground">Iteration</span>
    <div className="flex gap-1" role="tablist" aria-label="Iteration">
      {resultIds.map((id, index) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={id === activeId}
          onClick={() => onSelect(id)}
          className={cn(
            "size-6 cursor-pointer rounded text-xs tabular-nums transition-colors",
            id === activeId
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/70"
          )}
        >
          {index + 1}
        </button>
      ))}
    </div>
  </div>
);

/** Renders a flat JSONB blob as a definition list, nesting as formatted JSON. */
const KeyValueList = ({ record }: { record: Record<string, unknown> }) => (
  <dl className="flex flex-col gap-2">
    {Object.entries(record).map(([key, value]) => (
      <div key={key} className="flex flex-col gap-0.5">
        <dt className="text-xs font-medium text-muted-foreground">{humanizeKey(key)}</dt>
        <dd className="text-sm break-words whitespace-pre-wrap text-foreground">
          {typeof value === "object" && value !== null ? (
            <code className="block rounded bg-muted/60 p-2 font-mono text-[11px]">
              {JSON.stringify(value, null, 2)}
            </code>
          ) : (
            String(value)
          )}
        </dd>
      </div>
    ))}
  </dl>
);

/**
 * An evaluator's display name, from the key the runner posted it under — a
 * pydantic-evals evaluation name, which defaults to the class name. An
 * unrecognised key is shown verbatim, so it stays something a reader can grep for.
 */
function evaluatorLabel(name: string): string {
  return name in EVALUATORS ? EVALUATORS[name as keyof typeof EVALUATORS].label : name;
}

/** `task_duration_seconds` reads as `Task duration seconds`. */
function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
